import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import AvatarContainer from '@/assets/images/avatars/avatar_container.png';
import BeltFrameWhite from '@/assets/images/avatars/belt_frame_white.svg';
import BeltFrameBlue from '@/assets/images/avatars/belt_frame_blue.svg';
import BeltFramePurple from '@/assets/images/avatars/belt_frame_purple.svg';
import BeltFrameBrown from '@/assets/images/avatars/belt_frame_brown.svg';
import BeltFrameBlack from '@/assets/images/avatars/belt_frame_black.svg';
import WhiteBeltAvatar from '@/assets/images/avatars/white.png';

interface FramedAvatarProps {
  src?: string;
  alt?: string;
  initials?: string;
  size?: number; // avatar size (inner circle), default 42
  frameSize?: { w: number; h: number }; // frame size, default {w:60, h:66}
  className?: string;
  showFrame?: boolean;
  beltRank?: string;
  fontSize?: number; // font size for initials (px)
  // Navigation props
  username?: string; // If provided and !isManual, avatar becomes clickable and navigates to profile
  isManual?: boolean; // If true, avatar is not clickable even if username is provided
  onClick?: () => void; // Custom click handler (optional, overrides default navigation)
}

export const FramedAvatar: React.FC<FramedAvatarProps> = ({
  src,
  alt = '',
  initials = '',
  size,
  frameSize = { w: 60, h: 66 },
  className = '',
  showFrame = true,
  beltRank,
  fontSize,
  username,
  isManual = false,
  onClick,
}) => {
  let frameImg = BeltFrameWhite; // Default to white belt frame for manual partners
  if (showFrame && beltRank) {
    switch (beltRank.toLowerCase()) {
      case 'white':
        frameImg = BeltFrameWhite;
        break;
      case 'blue':
        frameImg = BeltFrameBlue;
        break;
      case 'purple':
        frameImg = BeltFramePurple;
        break;
      case 'brown':
        frameImg = BeltFrameBrown;
        break;
      case 'black':
        frameImg = BeltFrameBlack;
        break;
      default:
        frameImg = BeltFrameWhite; // Use white belt frame as default
    }
  }
  // Dynamically calculate avatar size if not provided: 75.7% of frame height (proportional)
  const avatarSize = size !== undefined ? size : Math.round((frameSize.h || 66) * 0.757);
  const translateY = '-translate-y-[57%]';
  // Calculate font size for initials (default: 40% of avatar size)
  const initialsFontSize = fontSize !== undefined ? fontSize : Math.round((avatarSize || 24) * 0.4);
  
  // Determine if avatar should be clickable
  const isClickable = !isManual && (username || onClick);
  const shouldNavigate = !isManual && username && !onClick;
  
  const avatarContent = (
    <div
      className={`relative flex-shrink-0 ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      style={{ width: frameSize.w, height: frameSize.h }}
      onClick={onClick ? (e) => {
        e.stopPropagation();
        onClick();
      } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable && !shouldNavigate ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) onClick();
        }
      } : undefined}
      aria-label={isClickable && username ? `View ${alt || username}'s profile` : undefined}
    >
      {src ? (
        <img
          key="avatar-image"
          src={src}
          alt={alt}
          className={`rounded-full border-2 border-black object-cover absolute left-1/2 top-1/2 -translate-x-1/2 ${translateY} z-10 ${className}`}
          style={{ width: avatarSize, height: avatarSize }}
        />
      ) : (
        <div
          key="avatar-default"
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 ${translateY} rounded-full flex items-center justify-center z-10 bg-[#1C1414] border border-[rgba(255,255,255,0.06)]`}
          style={{ width: avatarSize, height: avatarSize }}
        >
          <span 
            key="initials-text"
            className={`font-bold select-none text-white`} 
            style={{ fontSize: initialsFontSize }}
          >
            {initials}
          </span>
        </div>
      )}
      {showFrame && (
        <img
          key="avatar-frame"
          src={frameImg}
          alt="Avatar Frame"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-20"
          style={{ 
            width: frameSize.w, 
            height: frameSize.h
          }}
          onLoad={() => {
            if (beltRank?.toLowerCase() === 'white') {
              // console.log('🔄 FramedAvatar - Applied white belt frame');
            }
          }}
        />
      )}
    </div>
  );

  // Wrap in Link if should navigate, otherwise return content directly
  if (shouldNavigate) {
    return (
      <Link 
        to={`/profile/${username}`}
        onClick={(e) => e.stopPropagation()}
        className="flex-shrink-0"
        aria-label={`View ${alt || username}'s profile`}
      >
        {avatarContent}
      </Link>
    );
  }

  return avatarContent;
}; 